import SessionContext from '@providers/Session/SessionContext';
import { Button, Col, Row, Select, Spin, Tooltip, Empty } from 'antd';
import { forEach, groupBy } from 'lodash';
import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import useSwr from '@hooks/useSwr';
import { Column, ColumnConfig } from '@ant-design/plots';
import { Filters, FiltersCard } from '@components/Filters';
import {
  QuestionCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import useSearchParams from '@hooks/useSearchParams';
import { useGraphColors } from '@hooks/useGraphColors';
import * as S from './styles';

interface IStyleCss {
  [key: string]: CSSProperties;
}

interface ICharDataResponse {
  results: {
    series?: {
      columns: string[];
      name: string;
      tags: {
        [key: string]: string;
        macro_regiao: string;
        estado: string;
      };
      values: number[][];
    }[];
    statement_id: number;
  }[];
  totalValue: number;
}

interface ChartAnnotation {
  type: 'text';
  position: [string | number, string | number];
  style: {
    textAlign: 'center';
    fontSize: number;
    fill: string;
  };
  content: string;
  offsetY: number;
  tooltip: false;
}

const MIN_VALUE = 0;
const FIRST_INDEX = 0;
const SECOND_INDEX = 1;

const ConsumerTrafficByCustomerDashboard: React.FC = () => {
  const { setSearchParams, clearSearchParams, searchParams } = useSearchParams({
    extraParams: ['lastTime', 'groupByTime'],
  });

  const [formData, setFormData] = useState({
    groupByTime: '30d',
    lastTime: '365d',
  });

  const [showFilters, setShowFilters] = useState(true);
  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(),
  );

  const { t } = useTranslation(['date-picker', 'consumer-traffic-by-customer']);

  const { customersSwitch, selectedApp, theme, graphThemeKey } =
    useContext(SessionContext);
  const graphColors = useGraphColors();

  const requestParams = useMemo(
    () => ({
      params: {
        lastTime: formData?.lastTime,
        groupByTime: formData?.groupByTime,
        produto:
          ApplicationsConstants.FINANCIAL?.toUpperCase() !==
          selectedApp?.toUpperCase()
            ? selectedApp?.toUpperCase()
            : '',
      },
      headers: {
        customerId: customersSwitch?.selected,
      },
    }),
    [formData, customersSwitch, selectedApp],
  );

  const { data, isLoading } = useSwr<ICharDataResponse>(
    '/service-proxy/dashboard/grafico/financeiro/fatura_fechada/chips_cobrados/produto',
    {
      ...requestParams,
    },
  );

  const styles: IStyleCss = {
    titleCard: { lineHeight: '40px', fontSize: '1rem' },
    blank: { background: 'transparent' },
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    select: {
      width: '100%',
      maxWidth: '15rem',
    },
    spinner: {
      width: '100%',
      padding: '5rem 1rem',
      margin: 16,
    },
    charTitle: {
      margin: '1rem auto 0',
    },
    interval: { stroke: '#fff' },
  };

  const options = useMemo(
    () => ({
      lastTimeOptions: [
        { label: t('last 5 minutes', { minutes: 5 }), value: '5m' },
        { label: t('last 15 minutes', { minutes: 15 }), value: '15m' },
        { label: t('last 30 minutes', { minutes: 30 }), value: '30m' },
        { label: t('last 1 hour', { days: 1 }), value: '1h' },
        { label: t('last 24 hour', { hours: 24 }), value: '24h' },
        { label: t('last-week', { days: 7 }), value: '7d' },
        { label: t('last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('last-month', { days: 30 }), value: '30d' },
        { label: t('last-two-months', { days: 60 }), value: '60d' },
        { label: t('last-three-months', { days: 90 }), value: '90d' },
        { label: t('last-year', { days: 365 }), value: '365d' },
      ],
      groupedOptions: [
        { label: t('grouped.last-5-minutes', { minutes: 5 }), value: '5m' },
        { label: t('grouped.last-15-minutes', { minutes: 15 }), value: '15m' },
        { label: t('grouped.last-30-minutes', { minutes: 30 }), value: '30m' },
        { label: t('grouped.last-1-hour', { days: 1 }), value: '1h' },
        { label: t('grouped.last-24-hour', { hours: 24 }), value: '24h' },
        { label: t('grouped.last-week', { days: 7 }), value: '7d' },
        { label: t('grouped.last-two-weeks', { days: 14 }), value: '14d' },
        { label: t('grouped.last-three-weeks', { days: 21 }), value: '21d' },
        { label: t('grouped.last-month', { days: 30 }), value: '30d' },
        { label: t('grouped.last-two-months', { days: 60 }), value: '60d' },
        { label: t('grouped.last-three-months', { days: 90 }), value: '90d' },
        { label: t('grouped.last-year', { days: 365 }), value: '365d' },
      ],
    }),
    [t],
  );

  const getOptionsValue = useCallback(
    (searchParam: string, defaultValue: string) => {
      const isValidValue = options?.lastTimeOptions?.find(
        option => option?.value === searchParam,
      );

      if (!isValidValue && !!searchParam) {
        clearSearchParams();
        return defaultValue;
      }

      return isValidValue ? searchParam : defaultValue;
    },
    [options, clearSearchParams],
  );

  const filters = useMemo(
    () => [
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('consumer-traffic-by-customer:labelPeriod'),
        element: (
          <Select
            onChange={lastTime => {
              setFormData(state => ({ ...state, lastTime }));
              setSearchParams({
                lastTime,
              });
            }}
            value={getOptionsValue(searchParams?.lastTime, formData?.lastTime)}
            options={options?.lastTimeOptions}
            style={styles.select}
          />
        ),
      },
      {
        visible: true,
        col: {
          xs: 24,
          sm: 12,
          md: 8,
          lg: 6,
          xl: 5,
        },
        label: t('consumer-traffic-by-customer:labelGrouping'),
        element: (
          <Select
            onChange={groupByTime => {
              setFormData(state => ({ ...state, groupByTime }));
              setSearchParams({
                groupByTime,
              });
            }}
            options={options?.groupedOptions}
            value={getOptionsValue(
              searchParams?.groupByTime,
              formData?.groupByTime,
            )}
            style={styles.select}
          />
        ),
      },
    ],
    [
      formData?.groupByTime,
      formData?.lastTime,
      options,
      t,
      searchParams,
      styles.select,
      setSearchParams,
      getOptionsValue,
    ],
  );

  const formatBytes = useCallback((bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
  }, []);
  const getCharData = useCallback(() => {
    if (!data?.results || !data.results[FIRST_INDEX]?.series) {
      return [];
    }

    const charData = data.results[FIRST_INDEX].series
      .map(doc =>
        doc?.values?.map(value => {
          const listItem: { [key: string]: string | number } = {
            date: value[FIRST_INDEX],
            value: value[SECOND_INDEX] || MIN_VALUE,
            category: doc?.tags?.produto,
          };

          return listItem;
        }),
      )
      .flat();
    return charData || [];
  }, [data]);

  const allCategories = useMemo(() => {
    const chartData = getCharData();
    return [...new Set(chartData.map(item => item.category as string))].filter(
      Boolean,
    );
  }, [getCharData]);

  useEffect(() => {
    if (allCategories.length > 0) {
      setVisibleCategories(new Set(allCategories));
    }
  }, [allCategories]);

  const filteredChartData = useMemo(() => {
    const chartData = getCharData();
    return chartData.filter(item =>
      visibleCategories.has(item.category as string),
    );
  }, [getCharData, visibleCategories]);

  const handleCategoryToggle = useCallback((category: string) => {
    setVisibleCategories(prev => {
      const newVisible = new Set(prev);
      if (newVisible.has(category)) {
        newVisible.delete(category);
      } else {
        newVisible.add(category);
      }
      return newVisible;
    });
  }, []);

  const handleCategoryIsolate = useCallback(
    (category: string) => {
      if (visibleCategories.size === 1 && visibleCategories.has(category)) {
        setVisibleCategories(new Set(allCategories));
      } else {
        setVisibleCategories(new Set([category]));
      }
    },
    [visibleCategories, allCategories],
  );

  const getCategoryColor = useCallback(
    (category: string) => {
      const productColors = graphColors[graphThemeKey];
      const categoryIndex = allCategories.indexOf(category);
      return productColors[categoryIndex % productColors.length];
    },
    [graphColors, graphThemeKey, allCategories],
  );

  const config = useMemo(() => {
    const annotations: ChartAnnotation[] = [];
    forEach(groupBy(filteredChartData, 'date'), (values, date) => {
      const totalValue = values.reduce(
        (sum, item) => sum + (Number(item.value) || 0),
        0,
      );

      if (totalValue > 0) {
        annotations.push({
          type: 'text',
          position: [date, totalValue],
          style: {
            textAlign: 'center',
            fontSize: 12,
            fill: theme.colors.text,
          },
          content: formatBytes(totalValue),
          offsetY: -10,
          tooltip: false,
        });
      }
    });

    return {
      data: filteredChartData,
      xField: 'date',
      yField: 'value',
      seriesField: 'category',
      isStack: true,
      color: (datum: any) => {
        return getCategoryColor(datum.category);
      },
      meta: {
        date: {
          type: 'timeCat',
          mask: 'MM/YYYY',
        },
      },
      xAxis: {
        label: {
          autoHide: true,
          autoRotate: false,
          style: {
            fill: theme.colors.text,
          },
        },
      },
      yAxis: {
        label: {
          formatter: (val: string) => formatBytes(Number(val)),
          style: {
            fill: theme.colors.text,
          },
        },
      },
      legend: false,
      tooltip: {
        formatter: (datum: any) => {
          return {
            name: datum.category,
            value: formatBytes(datum.value),
          };
        },
      },
      annotations,
      interactions: [
        {
          type: 'element-active',
        },
      ],
    } as ColumnConfig;
  }, [filteredChartData, theme.colors.text, formatBytes, getCategoryColor]);

  const hasData = useMemo(() => !!config?.data?.length, [config?.data?.length]);

  const handleOpenFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const renderCustomLegend = () => {
    return (
      <S.LegendContainer>
        {allCategories.map(category => {
          const isVisible = visibleCategories.has(category);
          const color = getCategoryColor(category);
          const isIsolated =
            visibleCategories.size === 1 && visibleCategories.has(category);

          const getIcon = () => {
            if (!isVisible) {
              return <EyeInvisibleOutlined />;
            }
            if (isIsolated) {
              return <PushpinOutlined />;
            }
            return <EyeOutlined />;
          };

          return (
            <S.LegendItem key={category}>
              <S.LegendTag
                $isVisible={isVisible}
                $color={color}
                onClick={() => handleCategoryToggle(category)}
                title={category}
              >
                {category}
              </S.LegendTag>
              <S.LegendButton
                type="text"
                size="small"
                icon={getIcon()}
                $color={color}
                $isVisible={isVisible}
                onClick={() => {
                  if (!isVisible) {
                    handleCategoryToggle(category);
                  } else {
                    handleCategoryIsolate(category);
                  }
                }}
              />
            </S.LegendItem>
          );
        })}
      </S.LegendContainer>
    );
  };

  return (
    <S.ContainerRow gutter={[16, 16]}>
      <Col span={24}>
        <Row gutter={[16, 16]} justify="end">
          <Col span={24}>
            <S.CardContainer
              title={
                <div className="card-header">
                  <div className="card-title-box">
                    <div className="card-title">
                      {t('consumer-traffic-by-customer:traffic')}
                    </div>
                    <Tooltip
                      title={t('consumer-traffic-by-customer:tooltipMessage')}
                    >
                      <Button type="link" icon={<QuestionCircleOutlined />} />
                    </Tooltip>
                    <Filters isOpen={showFilters} onOpen={handleOpenFilters} />
                  </div>
                </div>
              }
            >
              <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24}>
                  {filters && (
                    <S.FiltersContainer>
                      <FiltersCard
                        isOpen={showFilters}
                        tableFilters={filters}
                      />
                    </S.FiltersContainer>
                  )}
                </Col>
              </Row>
              {isLoading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                  }}
                >
                  <Spin style={styles.spinner} />
                </div>
              ) : (
                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    {hasData ? (
                      <>
                        {renderCustomLegend()}
                        <Column
                          style={{
                            height: 400,
                          }}
                          {...config}
                        />
                      </>
                    ) : (
                      <Empty
                        description={t(
                          'consumer-traffic-by-customer:notFoundMessage',
                        )}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </Col>
                </Row>
              )}
            </S.CardContainer>
          </Col>
        </Row>
      </Col>
    </S.ContainerRow>
  );
};

export default ConsumerTrafficByCustomerDashboard;
